import { token } from '@atlaskit/tokens';
import { css } from '@emotion/react';
import { plainButton } from './button.styles';
import { Template } from '../constants/templates';
import type { FocusEvent, MouseEvent } from 'react';

interface TemplateButtonProps {
  index: number;
  template: Template;
  expanded: boolean;
  onMouseEnter: (
    template: Template,
    coordinates: DOMRect,
    templateIndex: number
  ) => void;
  onClick: () => void;
}

const templateButton = css`
  ${plainButton};
  width: 100%;
  padding: 4px;
`;

const templateButtonContainer = (expanded: boolean) => css`
  display: flex;
  padding: ${expanded ? '8px' : '0'};
  gap: 12px;
`;

const iconWrapper = css`
  border-radius: 4px;
  width: 32px;
  height: 32px;
  background-color: ${token('color.background.neutral')};
`;

const templateButtonInfo = css`
  display: flex;
  flex-direction: column;
  text-align: left;
  gap: 4px;
`;

const templateName = css`
  color: ${token('color.text')};
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
`;

const templateDescription = css`
  color: ${token('color.text.subtlest')};
  font-size: 12px;
  line-height: 16px;
`;

export const TemplateButton = ({
  index,
  template,
  expanded,
  onClick,
  onMouseEnter
}: TemplateButtonProps) => {
  const selectTemplate = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onClick();
  };

  const handleEnterFocus = (e: MouseEvent | FocusEvent) => {
    onMouseEnter(template, e.currentTarget.getBoundingClientRect(), index);
  };

  return (
    <button
      css={templateButton}
      onClick={selectTemplate}
      onMouseLeave={handleEnterFocus}
      onMouseEnter={handleEnterFocus}
      aria-labelledby={template.id + '-name'}
    >
      <div css={templateButtonContainer(expanded)}>
        <div css={iconWrapper}>
          <img width={32} height={32} src={template.icon} alt={template.name} />
        </div>
        {expanded && (
          <div css={templateButtonInfo}>
            <div id={template.id + '-name'} css={templateName}>
              {template.name}
            </div>
            <div css={templateDescription}>{template.description}</div>
          </div>
        )}
      </div>
    </button>
  );
};
